"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { isFlagOn, type FlagsMap } from "@/lib/feature-flags";
import { getConfigString } from "@/lib/remote-config";
import { useConfigs } from "./LandingConfigsProvider";
import {
  SOCIAL_ICONS,
  type SocialPlatformKey,
} from "./social-icons";
import type { SocialLink } from "@/lib/social-links";

interface FooterProps {
  /**
   * Evaluated feature flags for the LANDING platform. Used to hide
   * footer links whose target section is currently disabled, so users
   * don't click into a #anchor that doesn't exist.
   */
  flags?: FlagsMap;
  /**
   * Active social links fetched server-side from /v1/social-links and
   * passed in via the marketing layout. Empty array → footer hides
   * the socials block entirely instead of rendering an empty bar.
   */
  socialLinks?: SocialLink[];
}

/**
 * Site footer — client component.
 *
 * Renders the brand block, multi-column link sets, social icons and
 * legal notice. Footer columns and links can be hidden per evaluated
 * feature flag so the footer stays consistent with the visible sections.
 */
export default function Footer({
  flags = {},
  socialLinks = [],
}: FooterProps) {
  const t = useTranslations("footer");
  const configs = useConfigs();
  const cfg = (key: string, fallback: string) =>
    getConfigString(configs, key, fallback);

  const tagline = cfg("footer_tagline", t("tagline"));
  const legalNotice = cfg("footer_legal_notice", t("legalNotice"));

  // Each item optionally declares a `flag`. When the flag is OFF, the
  // link is filtered out of the column. Items without a flag (e.g. the
  // /producers page or a mailto:) are unaffected.
  type FooterItem = {
    label: string;
    href: string;
    flag?: string;
  };

  const sections: Array<{ title: string; items: FooterItem[] }> = [
    {
      title: cfg("footer_section_marketplace", t("sectionMarketplace")),
      items: (
        [
          {
            label: cfg("footer_link_eggtypes", t("linkEggTypes")),
            href: "/#catalogo",
            flag: "landing_section_catalog",
          },
          {
            label: cfg("footer_link_verification", t("linkVerification")),
            href: "/#verificacion",
            flag: "landing_section_verification",
          },
          {
            label: cfg("footer_link_howitworks", t("linkHowItWorks")),
            href: "/#como-funciona",
            flag: "landing_section_how_it_works",
          },
        ] as FooterItem[]
      ).filter((i) => !i.flag || isFlagOn(flags, i.flag)),
    },
    {
      title: cfg("footer_section_producers", t("sectionProducers")),
      items: (
        [
          {
            label: cfg("footer_link_apply", t("linkApply")),
            href: "/producers",
          },
          {
            label: cfg("footer_link_criteria", t("linkCriteria")),
            href: "/#verificacion",
            flag: "landing_section_verification",
          },
        ] as FooterItem[]
      ).filter((i) => !i.flag || isFlagOn(flags, i.flag)),
    },
    {
      title: cfg("footer_section_contact", t("sectionContact")),
      items: (
        [
          {
            label: cfg("footer_link_faq", t("linkFaq")),
            href: "/faq",
          },
          { label: "hola@urbaneggs.com.mx", href: "mailto:hola@urbaneggs.com.mx" },
          {
            label: cfg("footer_link_location", t("linkLocation")),
            href: "/#porque",
            flag: "landing_section_why_us",
          },
        ] as FooterItem[]
      ).filter((i) => !i.flag || isFlagOn(flags, i.flag)),
    },
  ];

  return (
    <footer className="bg-dark text-muted">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div>
            {/* Variante dark del logotipo: outline + texto + ojo en
                blanco; rellenos del huevo en transparent (dejan ver
                el dark del footer). Sin patch claro — el SVG está
                pensado para estar directamente sobre fondo oscuro. */}
            <Image
              src="/logo-on-dark.svg"
              alt="Urban Eggs"
              width={140}
              height={43}
              className="h-10 w-auto"
            />

            <p className="mt-4 text-sm leading-relaxed text-muted">
              {tagline}
            </p>
            {socialLinks.length > 0 && (
              <div className="mt-6 flex gap-4 flex-wrap">
                {socialLinks.map((link) => {
                  const meta =
                    SOCIAL_ICONS[link.platform as SocialPlatformKey];
                  if (!meta) return null;
                  return (
                    <a
                      key={link.id}
                      href={link.url}
                      target={
                        link.url.startsWith("mailto:") ? undefined : "_blank"
                      }
                      rel="noopener noreferrer"
                      aria-label={meta.label}
                      className="w-9 h-9 bg-[#2a2a28] hover:bg-primary rounded-lg flex items-center justify-center transition-colors text-muted hover:text-white"
                    >
                      {meta.icon}
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {sections.map((section) => (
            <div key={section.title}>
              <h4 className="text-white font-bold text-sm mb-4">
                {section.title}
              </h4>
              <ul className="space-y-3">
                {section.items.map((item, i) => {
                  const isExternal = item.href.startsWith("mailto:");
                  return (
                    <li key={`${section.title}-${i}`}>
                      {isExternal ? (
                        <a
                          href={item.href}
                          className="text-sm hover:text-mint transition-colors"
                        >
                          {item.label}
                        </a>
                      ) : (
                        <Link
                          href={item.href}
                          className="text-sm hover:text-mint transition-colors"
                        >
                          {item.label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-[#2a2a28] pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          <p>
            © {new Date().getFullYear()} Urban Eggs. {t("rights")}.
          </p>
          <div className="flex items-center gap-4">
            <Link href="/privacidad" className="hover:text-white transition-colors">
              {t("privacy")}
            </Link>
            <Link href="/terminos" className="hover:text-white transition-colors">
              {t("terms")}
            </Link>
            <Link
              href="/eliminacion-de-datos"
              className="hover:text-white transition-colors"
            >
              {t("dataDeletion")}
            </Link>
          </div>
          <p className="text-muted/50">{legalNotice}</p>
        </div>
      </div>
    </footer>
  );
}
